import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Segmented from "../../components/segmented";

// Views
import AccountSettings from "./views/AccountSettings";
import PersonalInfo from "./views/PersonalInfo";

// Context
import { UserProvider, useUser } from "../../contexts/UserContext";

// Types
import { User } from "../../types";

// Utils
import ThemeProvider from "../../utils/theme";

// Assets
import exitIcon from "../../../assets/images/exit.svg";

interface ProfileViewProps {
  user: User | null;
}

const ProfileViewContent: React.FC = () => {
  // Translations
  const { t } = useTranslation();

  // User Context
  const { user, updateUser } = useUser();

  // States
  const [selectedTab, setSelectedTab] = useState<'settings' | 'personal_infos'>('settings');

  // Memoized values
  const tabs = useMemo(() => [
    {
      label: t('profile.tabs.settings'),
      value: 'settings'
    },
    // {
    //   label: t('profile.tabs.personal_infos'),
    //   value: 'personal_infos'
    // }
  ], [t]);

  // Styles
  const rootClasses = "flex flex-col flex-1 gap-4 bg-white dark:bg-zeus-700 rounded-3xl p-4 h-full overflow-hidden cloudy:bg-coffee-300";
  return (
    <ThemeProvider>
      <div className={rootClasses}>
        <div className="md:flex hidden flex-row gap-2 items-center justify-between">
          <h1 className="text-2xl text-gray-900 dark:text-white">
            {t('profile.title')}
          </h1>
          <button
            onClick={async () => {
              try {
                const response = await fetch('/users/sign_out', {
                  method: 'DELETE',
                  headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    'Content-Type': 'application/json',
                  },
                });

                if (response.ok) {
                  Turbo.visit('/');
                }
              } catch (error) {
              }
            }}
            className="group p-2 bg-gray-200 hover:bg-blue-600 text-white rounded-full transition-colors duration-200 font-medium flex items-center gap-2 dark:bg-stone-650 dark:hover:bg-zeus-900"
          >
            <img src={exitIcon} alt="Exit" className="w-5 h-5 filter brightness-0 saturate-100 invert-[0.4] group-hover:brightness-0 group-hover:saturate-100 group-hover:invert group-hover:hue-rotate-[200deg] dark:invert dark:brightness-100 dark:saturate-100" />
          </button>
        </div>

        <Segmented
          options={tabs}
          value={selectedTab}
          onChange={(value) => setSelectedTab(value as 'settings' | 'personal_infos')}
        />

        {selectedTab === 'settings' && <AccountSettings user={user} onUpdateUser={updateUser} />}

        {selectedTab === 'personal_infos' && <PersonalInfo user={user} />}
      </div>
    </ThemeProvider>
  );
};

const ProfileView: React.FC<ProfileViewProps> = ({ user }) => {
  return (
    <UserProvider initialUser={user}>
      <ProfileViewContent />
    </UserProvider>
  );
};

export default ProfileView;
